import { useEffect } from 'react'
import { sendWorkspaceFocusHeartbeat } from '../lib/api'

const HEARTBEAT_INTERVAL_MS = 10_000

/**
 * Tell the server "I'm focused on this app's workspace tab" while it's true.
 *
 * The Router uses this signal to suppress claude_* notifications — if the user
 * is already watching Claude work, dropping a bell row would just spam the
 * notification list when they navigate away. We deliberately gate on
 * `document.visibilityState === 'visible'`, not just "this component is
 * mounted", because a backgrounded workspace tab is no different from a closed
 * one as far as the user is concerned.
 *
 * Server key TTL is 20s; heartbeating every 10s leaves one full refresh of
 * headroom. On blur we don't proactively delete — the key expires within at
 * most 20s, which is short enough that the user doesn't silently miss
 * notifications and long enough to ride out a single missed heartbeat under
 * network jitter.
 */
export function useWorkspaceFocusHeartbeat(appName: string | null | undefined): void {
  useEffect(() => {
    if (!appName) return

    let timer: ReturnType<typeof setInterval> | null = null
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      if (document.visibilityState !== 'visible') return
      void sendWorkspaceFocusHeartbeat(appName).catch(() => {
        // Swallow — the server fails open on Redis errors, so a missed
        // heartbeat just means the user might get one extra notification.
      })
    }

    const start = () => {
      tick()
      if (timer === null) {
        timer = setInterval(tick, HEARTBEAT_INTERVAL_MS)
      }
    }

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Re-fire immediately so the suppression turns back on without waiting
        // for the next tick — feels snappier when the user tabs back.
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === 'visible') {
      start()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      stop()
    }
  }, [appName])
}
